import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Col, Form, Row } from "react-bootstrap";
import { get } from "helpers";
import {
  getMaisForneceramFeedback,
  getMaisReceberamFeedback,
  getResumoTotalFeedback,
} from "helpers/api/rankingFeedbackApi";
import {
  IPagination,
  IPeriodoFeedback,
  IRankingForneceuItem,
  IRankingRecebeuItem,
  IResumoTotalFeedbackResponse,
  ITipoFeedback,
} from "types";
import DestaquesFeedback from "./DestaquesFeedback";
import ListaRanking, { IItemRanking } from "./ListaRanking";
import GraficoResumo from "./GraficoResumo";
import { VerifyUserAdmOuDesenvolvedor } from "utils/verify-user-adm";
import {
  formatarDataBR,
  periodoDoPreset,
  PresetPeriodo,
  PRESETS_PERIODO,
  PRESET_PADRAO,
  validarPeriodo,
} from "./periodo";

interface IEstadoRequisicao<T> {
  carregando: boolean;
  erro: string | null;
  dados: T | null;
}

const ESTADO_INICIAL = { carregando: true, erro: null, dados: null };

const TODOS_OS_TIPOS = 0;

const itensReceberam = (
  itens: IRankingRecebeuItem[] | null
): IItemRanking[] | null =>
  itens &&
  itens.map(({ colaborador_id, nome, quantidade }) => ({
    chave: `colaborador-${colaborador_id}`,
    nome,
    quantidade,
  }));

const itensForneceram = (
  itens: IRankingForneceuItem[] | null
): IItemRanking[] | null =>
  itens &&
  itens.map(({ usuario_id, nome, quantidade }) => ({
    chave: `usuario-${usuario_id}`,
    nome,
    quantidade,
  }));

const mensagemErro = (error: any, padrao: string): string =>
  error?.response?.data?.error ?? padrao;

const RankingFeedbacks: React.FC = () => {
  // Só administradores e desenvolvedores filtram por tipo e veem os rankings
  // completos (Top 3 / Top 10 / todos); os demais veem os campeões e o resumo
  // de todos os tipos.
  const ehAdmOuDesenvolvedor = VerifyUserAdmOuDesenvolvedor();
  const [preset, setPreset] = useState<PresetPeriodo>(PRESET_PADRAO);
  const [periodoCustom, setPeriodoCustom] = useState<IPeriodoFeedback>(
    periodoDoPreset("30d")
  );
  const [tipoId, setTipoId] = useState(TODOS_OS_TIPOS);
  const [tipos, setTipos] = useState<ITipoFeedback[]>([]);
  const [erroTipos, setErroTipos] = useState<string | null>(null);

  const [receberam, setReceberam] =
    useState<IEstadoRequisicao<IRankingRecebeuItem[]>>(ESTADO_INICIAL);
  const [forneceram, setForneceram] =
    useState<IEstadoRequisicao<IRankingForneceuItem[]>>(ESTADO_INICIAL);
  const [resumo, setResumo] =
    useState<IEstadoRequisicao<IResumoTotalFeedbackResponse>>(ESTADO_INICIAL);

  // null = todo o período: a API não recebe datas e considera todo o histórico.
  const periodo = useMemo(() => {
    if (preset === "todo") return null;
    return preset === "custom" ? periodoCustom : periodoDoPreset(preset);
  }, [preset, periodoCustom]);
  const erroPeriodo = periodo ? validarPeriodo(periodo) : null;

  const nomeTipo =
    tipoId === TODOS_OS_TIPOS
      ? "Todos os tipos"
      : tipos.find((t) => t.id === tipoId)?.tipo ?? `Tipo #${tipoId}`;
  const legendaPeriodo = !periodo
    ? "Todo o período"
    : erroPeriodo
    ? "Período inválido"
    : `${formatarDataBR(periodo.data_inicio)} a ${formatarDataBR(
        periodo.data_fim
      )}`;
  const legendaFiltro = `${nomeTipo} · ${legendaPeriodo}`;

  const listaReceberam = useMemo(
    () => itensReceberam(receberam.dados),
    [receberam.dados]
  );
  const listaForneceram = useMemo(
    () => itensForneceram(forneceram.dados),
    [forneceram.dados]
  );

  useEffect(() => {
    if (!ehAdmOuDesenvolvedor) return;
    get<IPagination<ITipoFeedback>>("/tipo-feedback", { pageSize: 100 })
      .then(({ data }) => setTipos(data.result ?? []))
      .catch((error) =>
        setErroTipos(
          mensagemErro(error, "Não foi possível carregar os tipos de feedback.")
        )
      );
  }, [ehAdmOuDesenvolvedor]);

  useEffect(() => {
    if (erroPeriodo) return;

    // Descarta respostas de filtros antigos (troca rápida) e após desmontar.
    let ativo = true;

    // Mantém os dados atuais na tela enquanto busca os novos: destaques e
    // gráfico só esmaecem e fazem a transição, em vez de sumir e remontar.
    const recarregar = <T,>(atual: IEstadoRequisicao<T>) => ({
      ...atual,
      carregando: true,
      erro: null,
    });
    setReceberam(recarregar);
    setForneceram(recarregar);
    setResumo(recarregar);

    const filtro = { ...periodo, tipo_id: tipoId };

    const buscar = <R, T>(
      requisicao: Promise<{ data: R }>,
      setEstado: (estado: IEstadoRequisicao<T>) => void,
      extrair: (data: R) => T
    ) =>
      requisicao
        .then(
          ({ data }) =>
            ativo &&
            setEstado({ carregando: false, erro: null, dados: extrair(data) })
        )
        .catch(
          (error) =>
            ativo &&
            setEstado({
              carregando: false,
              erro: mensagemErro(error, "Erro ao gerar dashboard"),
              dados: null,
            })
        );

    buscar(
      getMaisReceberamFeedback(filtro),
      setReceberam,
      (data) => data.porUsuario ?? []
    );
    buscar(
      getMaisForneceramFeedback(filtro),
      setForneceram,
      (data) => data.porUsuario ?? []
    );
    buscar(getResumoTotalFeedback(filtro), setResumo, (data) => data);

    return () => {
      ativo = false;
    };
    // periodo é derivado das datas; as dependências usam as strings para não refazer à toa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo?.data_inicio, periodo?.data_fim, tipoId, erroPeriodo]);

  const alterarPreset = (novo: PresetPeriodo) => {
    // Ao abrir o personalizado, parte do período que estava aplicado.
    if (novo === "custom" && periodo && !erroPeriodo) setPeriodoCustom(periodo);
    setPreset(novo);
  };

  return (
    <div>
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col lg={3} md={6}>
              <Form.Label htmlFor="ranking-preset">Período</Form.Label>
              <Form.Select
                id="ranking-preset"
                value={preset}
                onChange={(e) => alterarPreset(e.target.value as PresetPeriodo)}
              >
                {PRESETS_PERIODO.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            {preset === "custom" && (
              <>
                <Col lg={2} md={3} xs={6}>
                  <Form.Label htmlFor="ranking-inicio">Data inicial</Form.Label>
                  <Form.Control
                    id="ranking-inicio"
                    type="date"
                    value={periodoCustom.data_inicio}
                    max={periodoCustom.data_fim || undefined}
                    isInvalid={!!erroPeriodo}
                    onChange={(e) =>
                      setPeriodoCustom({
                        ...periodoCustom,
                        data_inicio: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col lg={2} md={3} xs={6}>
                  <Form.Label htmlFor="ranking-fim">Data final</Form.Label>
                  <Form.Control
                    id="ranking-fim"
                    type="date"
                    value={periodoCustom.data_fim}
                    min={periodoCustom.data_inicio || undefined}
                    isInvalid={!!erroPeriodo}
                    onChange={(e) =>
                      setPeriodoCustom({
                        ...periodoCustom,
                        data_fim: e.target.value,
                      })
                    }
                  />
                </Col>
              </>
            )}
            {ehAdmOuDesenvolvedor && (
              <Col lg={3} md={6}>
                <Form.Label htmlFor="ranking-tipo">Tipo de feedback</Form.Label>
                <Form.Select
                  id="ranking-tipo"
                  value={tipoId}
                  onChange={(e) => setTipoId(Number(e.target.value))}
                >
                  <option value={TODOS_OS_TIPOS}>Todos os tipos</option>
                  {tipos.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.tipo}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            )}
          </Row>

          {erroPeriodo && (
            <Alert variant="warning" className="mt-3 mb-0">
              {erroPeriodo}
            </Alert>
          )}
          {erroTipos && (
            <Alert variant="danger" className="mt-3 mb-0">
              {erroTipos} Só é possível filtrar por "Todos os tipos".
            </Alert>
          )}
        </Card.Body>
      </Card>

      {erroPeriodo ? null : (
        <>
          <Row className="g-3 mb-3">
            <Col xs={12}>
              <DestaquesFeedback
                legendaFiltro={legendaFiltro}
                receberam={{
                  carregando: receberam.carregando,
                  erro: receberam.erro,
                  itens: listaReceberam,
                }}
                forneceram={{
                  carregando: forneceram.carregando,
                  erro: forneceram.erro,
                  itens: listaForneceram,
                }}
              >
                <GraficoResumo
                  tipoFiltrado={tipoId !== TODOS_OS_TIPOS}
                  carregando={resumo.carregando}
                  erro={resumo.erro}
                  resumo={resumo.dados}
                />
              </DestaquesFeedback>
            </Col>
          </Row>

          {ehAdmOuDesenvolvedor && (
            <Row className="g-3">
              <Col lg={6}>
                <ListaRanking
                  key={`receberam-${legendaFiltro}`}
                  titulo="Mais receberam"
                  icone="mdi mdi-star"
                  cor="info"
                  descricaoRotulo=""
                  legendaFiltro={legendaFiltro}
                  carregando={receberam.carregando}
                  erro={receberam.erro}
                  itens={listaReceberam}
                />
              </Col>
              <Col lg={6}>
                <ListaRanking
                  key={`forneceram-${legendaFiltro}`}
                  titulo="Mais forneceram"
                  icone="mdi mdi-trophy"
                  cor="primary"
                  descricaoRotulo=""
                  legendaFiltro={legendaFiltro}
                  carregando={forneceram.carregando}
                  erro={forneceram.erro}
                  itens={listaForneceram}
                />
              </Col>
            </Row>
          )}
        </>
      )}
    </div>
  );
};

export default RankingFeedbacks;
